import { useEffect, useState } from "react";
import moment from "moment-timezone";
import Decimal from "decimal.js";
import { AlertCircle, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader } from "@/components/ui/loader";
import { SelectInput } from "@/components/ui/SelectInput";
import {
  CreateExpirationPositionAdjustmentValidation,
  ExpirationPositionAdjustment,
  ExpirationPositionAdjustmentLeg,
  useCreateExpirationPositionAdjustment,
  useDeletePositionAdjustment,
  useListExpirationPositionAdjustments,
} from "@/lib/api/otc";
import { CommonDestinationTimezone } from "@/lib/api/utils";
interface ExpirationAdjustmentsModalProps {
  open: boolean;
  layerId: string | null;
  allowCreate: boolean;
  onClose: () => void;
  onChanged: () => void;
}

const LEG_LABELS: Record<ExpirationPositionAdjustmentLeg, string> = {
  step_one: "Step 1",
  step_two: "Step 2",
  step_three: "Step 3",
  double_up: "Double Up",
};

function formatDate(value: string | null): string {
  if (!value) return "—";
  const parsed = moment.tz(value, CommonDestinationTimezone);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY") : "—";
}

function formatQuantity(value: string | null): string {
  if (value === null) return "—";
  try {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 }).format(
      new Decimal(value).toNumber(),
    );
  } catch {
    return value;
  }
}

export function ExpirationAdjustmentsModal({
  open,
  layerId,
  allowCreate,
  onClose,
  onChanged,
}: ExpirationAdjustmentsModalProps) {
  const adjustmentsQuery = useListExpirationPositionAdjustments(
    open && layerId ? { layer_id: layerId } : null,
  );
  const createAdjustment = useCreateExpirationPositionAdjustment();
  const deleteAdjustment = useDeletePositionAdjustment();
  const [leg, setLeg] = useState<ExpirationPositionAdjustmentLeg | "">("");
  const [quantityOverride, setQuantityOverride] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);

  const result = adjustmentsQuery.data;
  const adjustments = result?.success ? result.data : [];
  const adjustedLegs = new Set(
    adjustments
      .map(
        (adjustment: ExpirationPositionAdjustment): string | null =>
          adjustment.contract_leg,
      )
      .filter((adjustmentLeg: string | null | undefined): adjustmentLeg is string => adjustmentLeg != null),
  );
  const legOptions = Object.entries(LEG_LABELS)
    .filter(
      ([step]: [string, string]): boolean => !adjustedLegs.has(step),
    )
    .map(([value, label]: [string, string]) => ({
      value: value as ExpirationPositionAdjustmentLeg,
      label,
    }));

  useEffect(() => {
    if (open) {
        setLeg((currentLeg: ExpirationPositionAdjustmentLeg | "") =>
            legOptions.some(
                (option: { value: ExpirationPositionAdjustmentLeg; label: string }): boolean =>
                option.value === currentLeg,
            )
                ? currentLeg
                : legOptions[0]?.value ?? "",
        );
        setQuantityOverride("");
        setSubmitError(null);
        setDeletingId(null);
        setConfirmingDeleteId(null);
    }
  }, [open, layerId, result]);

  const busy = isSaving || deletingId !== null;

  const handleCreate = async () => {
    if (!layerId || isSaving) return;
    setIsSaving(true);
    setSubmitError(null);

    try {
        const parsed = CreateExpirationPositionAdjustmentValidation.safeParse({
            layer_id: layerId,
            leg,
            position_uom_quantity_override: quantityOverride,
        });
        if (!parsed.success) {
            setSubmitError(parsed.error.issues[0]?.message ?? "Invalid expiration adjustment.");
            return;
        }

      const createResult = await createAdjustment(parsed.data);
      if (!createResult.success) {
        setSubmitError(createResult.message || "Failed to create expiration adjustment.");
        return;
      }
      setQuantityOverride("");
      await adjustmentsQuery.refetch();
      onChanged();
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (adjustmentId: string) => {
    if (busy || !!deletingId) return;
    try {
      setDeletingId(adjustmentId);
      setSubmitError(null);

      const deleteResult = await deleteAdjustment(adjustmentId);
      if (!deleteResult.success) {
        setSubmitError(deleteResult.message || "Failed to delete expiration adjustment.");
        return;
      }
      setConfirmingDeleteId(null);
      await adjustmentsQuery.refetch();
      onChanged();
    } finally {
      setDeletingId(null);
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && !busy) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-none w-[75vw] max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Expiration Adjustments</DialogTitle>
        </DialogHeader>

        <div className="grow overflow-y-auto pr-1 flex flex-col gap-4">
          {submitError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Something went wrong</AlertTitle>
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}

          {adjustmentsQuery.isLoading && "Loading..."}

          {!adjustmentsQuery.isLoading && result && !result.success && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Unable to load expiration adjustments</AlertTitle>
              <AlertDescription>{result.message || "An unexpected error occurred."}</AlertDescription>
            </Alert>
          )}

          {!adjustmentsQuery.isLoading && result?.success && (
            <div className="rounded-lg border overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/50 text-left">
                  <tr>
                    <th className="p-3">Leg</th>
                    <th className="p-3">Instrument Expiration</th>
                    <th className="p-3">Commodity</th>
                    <th className="p-3">Option Month</th>
                    <th className="p-3">Strike</th>
                    <th className="p-3">Put/Call</th>
                    <th className="p-3">Long</th>
                    <th className="p-3">Short</th>
                    <th className="p-3">Bushels</th>
                    <th className="p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {adjustments.map((adjustment: ExpirationPositionAdjustment) => (
                    <tr key={adjustment.id} className="border-b last:border-b-0">
                      <td className="p-3">
                        {adjustment.contract_leg && adjustment.contract_leg in LEG_LABELS
                          ? LEG_LABELS[adjustment.contract_leg as ExpirationPositionAdjustmentLeg]
                          : adjustment.contract_leg || "—"}
                      </td>
                      <td className="p-3">{formatDate(adjustment.instrument_expiration_date)}</td>
                      <td className="p-3">{adjustment.commodity}</td>
                      <td className="p-3">{adjustment.option_month}</td>
                      <td className="p-3">{formatQuantity(adjustment.strike_price)}</td>
                      <td className="p-3">{adjustment.put_or_call || "—"}</td>
                      <td className="p-3">{formatQuantity(adjustment.total_long_qty)}</td>
                      <td className="p-3">{formatQuantity(adjustment.total_short_qty)}</td>
                      <td className="p-3">{formatQuantity(adjustment.total_bushel)}</td>
                      <td className="p-3">
                        {confirmingDeleteId === adjustment.id ? (
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="destructive"
                              disabled={busy}
                              onClick={() => handleDelete(adjustment.id)}
                            >
                              {deletingId === adjustment.id ? "Deleting..." : "Confirm"}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              disabled={busy}
                              onClick={() => setConfirmingDeleteId(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            disabled={busy}
                            onClick={() => setConfirmingDeleteId(adjustment.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {adjustments.length === 0 && (
                    <tr>
                      <td colSpan={12} className="p-6 text-center text-muted-foreground">
                        No active expiration adjustments.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {allowCreate && layerId && (
            <div className="rounded-lg border p-4 flex flex-col gap-3">
              <span className="font-medium">Create Expiration Adjustment</span>
              {legOptions.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="grid gap-1.5">
                    <Label>Contract Leg</Label>
                    <SelectInput
                      value={leg}
                      options={legOptions}
                      onValueChange={(value: string) =>
                        setLeg(value as ExpirationPositionAdjustmentLeg)
                      }
                      disabled={busy}
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <Label>Bushel/Lbs Quantity Override</Label>
                    <Input
                      value={quantityOverride}
                      onChange={(event) => setQuantityOverride(event.target.value)}
                      placeholder="Optional"
                      inputMode="numeric"
                      disabled={busy}
                    />
                  </div>
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">
                  This layer has no eligible contract legs.
                </span>
              )}
              <div>
                <Button
                  type="button"
                  onClick={handleCreate}
                  disabled={busy || legOptions.length === 0 || leg === ""}
                >
                  {isSaving ? "Creating..." : "Create Adjustment"}
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
